import type {FC} from 'react';
import {useCallback, useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {Box, Stack} from '@wellstone-solutions/web';
import {Hooks} from '@wellstone-solutions/common';
import type {ApiResponseType} from '@wellstone-solutions/common';
import type {UIResourceType} from '@wellstone-solutions/common/models/rest';
import {useStores} from 'hooks/useStores';
import {PageHeader} from 'components/PageHeader';
import {LoadingIndicator} from 'components/shared/LoadingIndicator';
import {ErrorPage} from 'components/ErrorPage';
import {ALERT_TYPES, showAlert} from 'utils/showAlert';
import {
  ActionButtons,
  ResourceForm,
  parseValidation,
  resourceSchema,
  showSavedAlert,
  toFormValues,
} from '../ResourceForm';
import type {ResourceFormHelpers} from '../ResourceForm/types';

const FORM_ID = 'edit_resource_form';

export const EditResource: FC = () => {
  const {appUIStore, resourceStoreV2} = useStores();
  const {id = ''} = useParams();

  const [resource, setResource] = useState<UIResourceType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    appUIStore.setBreadcrumbs([]);
  }, [appUIStore]);

  useEffect(() => {
    resourceStoreV2.getCollections();
  }, [resourceStoreV2]);

  useEffect(() => {
    let isCurrent = true;
    setIsLoading(true);

    resourceStoreV2
      .getResource(id)
      .then((response: ApiResponseType<UIResourceType | null>) => {
        if (!isCurrent) {
          return;
        }

        if (response.isSuccess && response.data) {
          setResource(response.data);
          setHasError(false);
        } else {
          setHasError(true);
        }

        setIsLoading(false);
      });

    return () => {
      isCurrent = false;
    };
  }, [id, resourceStoreV2]);

  if (isLoading) {
    return <LoadingIndicator />;
  }

  if (hasError || !resource) {
    return <ErrorPage message="Resource not found" isFullScreen={false} />;
  }

  return <EditResourceForm key={resource.id} resource={resource} />;
};

// Mounted only once the resource has loaded: the description's RichTextEditor
// reads `initialValue` on mount only, so the form must start with real values.
const EditResourceForm: FC<{resource: UIResourceType}> = ({resource}) => {
  const {resourceStoreV2} = useStores();
  const navigate = useNavigate();
  const [isDeleting, setIsDeleting] = useState(false);

  const form = Hooks.useForm({
    initialValues: toFormValues(resource),
    initialStatus: {apiErrors: parseValidation(resource.validation)},
    schema: resourceSchema,
    validationEngine: 'yup',
    onSubmit: async (
      values: UIResourceType,
      {resetForm}: ResourceFormHelpers,
    ) => {
      const response = await resourceStoreV2.updateResource(values);

      if (response.isSuccess && response.data) {
        const saved = response.data;
        const apiErrors = showSavedAlert(saved.validation);

        resetForm({values: toFormValues(saved), status: {apiErrors}});
      } else {
        showAlert('Failed to save resource', ALERT_TYPES.ERROR);
      }
    },
  });

  const handleDelete = useCallback(async () => {
    const isConfirmed = window.confirm(
      'Are you sure you want to delete this resource?',
    );

    if (!isConfirmed) {
      return;
    }

    setIsDeleting(true);
    const response = await resourceStoreV2.deleteResource(resource.id);

    if (response.isSuccess) {
      showAlert('Resource deleted successfully', ALERT_TYPES.SUCCESS);
      navigate('/resources');
    } else {
      setIsDeleting(false);
      showAlert('Failed to delete resource', ALERT_TYPES.ERROR);
    }
  }, [navigate, resource.id, resourceStoreV2]);

  return (
    <Box>
      <Stack direction="column" sx={{mb: 4}}>
        <PageHeader
          title="Edit Resource"
          description="All fields are optional unless indicated as required."
          backButtonProps={{label: 'Back to Resources', path: '/resources'}}
        />

        <ResourceForm form={form} formId={FORM_ID} />

        <ActionButtons
          form={form}
          formId={FORM_ID}
          isDeleting={isDeleting}
          onDelete={() => {
            handleDelete();
          }}
        />
      </Stack>
    </Box>
  );
};
